import type { AlarmsListResponse, EventDestination } from "@starsach/shared-types";
import { useAlarms, useEvents } from "../../api/hooks";
import { Card, DataTable, DayWindowToggle, EmptyState, ErrorState, LoadingState, RelativeTime, SeverityPill, StatusPill } from "../../components/ui";
import { useUiStore } from "../../store/ui";

export function AlarmsTab({ environmentId }: { environmentId: string }) {
  const window = useUiStore((s) => s.window);
  const setWindow = useUiStore((s) => s.setWindow);
  const alarms = useAlarms(environmentId);
  const events = useEvents(environmentId, "cloudwatch_alarm", window);
  const open = alarms.data?.alarms.filter((a) => a.state === "OPEN") ?? [];
  const resolved = alarms.data?.alarms.filter((a) => a.state !== "OPEN") ?? [];

  return (
    <div className="grid gap-6">
      {alarms.isPending && <LoadingState />}
      {alarms.isError && <ErrorState error={alarms.error} onRetry={() => void alarms.refetch()} />}
      {alarms.data && (
        <>
          {/* Open and resolved are different questions - what needs someone now,
              and what has already cleared - so they are two lists, not one
              column to scan. Six come in and three resolve: three here, three below. */}
          <Card title={`Open alarms (${open.length})`}>
            <DataTable
              caption="Alarms currently in ALARM state"
              rows={open}
              rowKey={(a) => a.alarm_arn}
              sortBy={(a) => a.alarm_name}
              empty={<p className="py-3 text-sm text-emerald-700 dark:text-emerald-300">Nothing open - every alarm that has fired has since resolved.</p>}
              columns={[
                { key: "name", header: "Alarm", render: (a) => <span className="font-medium">{a.alarm_name}</span> },
                { key: "sev", header: "Severity", render: (a) => <SeverityPill severity={a.severity} /> },
                { key: "opened", header: "Opened", render: (a) => <RelativeTime iso={a.opened_at} /> },
                { key: "seen", header: "Last seen", render: (a) => <RelativeTime iso={a.last_seen_at} /> },
                { key: "count", header: "Occurrences", numeric: true, render: (a) => a.occurrence_count },
                { key: "ticket", header: "Ticket", render: (a) => <TicketCell alarm={a} /> },
              ]}
            />
          </Card>
          <Card title={`Resolved (${resolved.length})`}>
            <DataTable
              caption="Alarms that fired and have since returned to OK"
              rows={resolved}
              rowKey={(a) => a.alarm_arn}
              sortBy={(a) => a.alarm_name}
              rowClass={() => "text-neutral-500 dark:text-neutral-400"}
              empty={<EmptyState title="No alarms have fired for this account" hint="The baseline alarm set reports here the first time one trips." />}
              columns={[
                { key: "name", header: "Alarm", render: (a) => a.alarm_name },
                { key: "sev", header: "Severity", render: (a) => <SeverityPill severity={a.severity} /> },
                { key: "opened", header: "Opened", render: (a) => <RelativeTime iso={a.opened_at} /> },
                { key: "resolved", header: "Resolved", render: (a) => <StatusPill status={a.resolved_at ? "resolved" : "ok"} tone="ok" /> },
                { key: "when", header: "Resolved at", render: (a) => <RelativeTime iso={a.resolved_at ?? a.last_seen_at} /> },
                { key: "count", header: "Occurrences", numeric: true, render: (a) => a.occurrence_count },
                { key: "ticket", header: "Ticket", render: (a) => <TicketCell alarm={a} /> },
              ]}
            />
          </Card>
        </>
      )}
      <Card title="Alarm events">
        <div className="mb-3 flex justify-end"><DayWindowToggle value={window} onChange={setWindow} /></div>
        {events.isPending && <LoadingState />}
        {events.isError && <ErrorState error={events.error} onRetry={() => void events.refetch()} />}
        {events.data && (
          <DataTable
            caption={`Alarm state changes in the last ${window} days`}
            rows={events.data.events}
            rowKey={(e) => e.eventId}
            empty={<EmptyState title={`No alarm events in the last ${window} days`} />}
            columns={[
              { key: "when", header: "When", render: (e) => <RelativeTime iso={e.timestamp} /> },
              { key: "alarm", header: "Alarm", render: (e) => e.title },
              { key: "state", header: "State", render: (e) => <StatusPill status={e.state ?? "-"} tone={e.state === "ALARM" ? "critical" : e.state === "OK" ? "ok" : "neutral"} /> },
              { key: "sev", header: "Severity", render: (e) => <SeverityPill severity={e.severity} /> },
              { key: "resource", header: "Resource", render: (e) => <span className="font-mono text-xs">{e.resource ?? "-"}</span> },
              { key: "reason", header: "Reason", render: (e) => <span className="text-neutral-600 dark:text-neutral-400">{e.reason ?? ""}</span> },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

/** What a person calls a ticket in each backend, for a row whose number is not known yet. */
const TICKET_KIND: Record<EventDestination, string> = { connect_cases: "Connect case", none: "Ticket" };

/**
 * #525: the case number agents see in Connect. A row opened before the
 * number was recorded names the kind of ticket instead, with the backend's
 * id on hover - never the raw destination id.
 */
function TicketCell({ alarm }: { alarm: AlarmsListResponse["alarms"][number] }) {
  if (!alarm.hasTicket) return <>none</>;
  if (alarm.ticketNumber) return <span className="font-mono">{alarm.ticketNumber}</span>;
  return <span title={alarm.ticketRef}>{TICKET_KIND[alarm.destination]}</span>;
}
